const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

export interface Customer {
  id: string;
  name: string;
  phone: string;
  email?: string;
  vehicle_make?: string;
  vehicle_model?: string;
  vehicle_year?: number;
  last_service_date?: string;
  service_due_date?: string;
  sms_consent: boolean;
  whatsapp_consent: boolean;
  created_at: string;
  updated_at: string;
}

export interface CustomerUploadResponse {
  imported: number;
  failed: number;
  errors: Array<{ row?: number; name?: string; error: string }>;
}

class CustomerService {
  private getAuthHeaders(): Record<string, string> {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }

  async uploadCSV(file: File): Promise<CustomerUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${API_BASE_URL}/v1/customers/upload/csv`, {
        method: 'POST',
        headers: this.getAuthHeaders() as HeadersInit,
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to upload customers');
      }

      return await response.json();
    } catch (error) {
      console.error('Error uploading customers:', error);
      throw error;
    }
  }

  async uploadJSON(customers: any[]): Promise<CustomerUploadResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/customers/upload`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeaders(),
        } as HeadersInit,
        body: JSON.stringify({ customers }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to upload customers');
      }

      return await response.json();
    } catch (error) {
      console.error('Error uploading customers:', error);
      throw error;
    }
  }

  async getCustomers(skip = 0, limit = 100): Promise<Customer[]> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/v1/customers/?skip=${skip}&limit=${limit}`,
        {
          headers: {
            ...this.getAuthHeaders(),
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch customers');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching customers:', error);
      return [];
    }
  }

  async getCustomer(id: string): Promise<Customer | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/customers/${id}`, {
        headers: this.getAuthHeaders() as HeadersInit,
      });

      if (!response.ok) {
        throw new Error('Failed to fetch customer');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching customer:', error);
      return null;
    }
  }

  async updateConsent(
    customerId: string,
    smsConsent?: boolean,
    whatsappConsent?: boolean
  ): Promise<Customer | null> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/v1/customers/${customerId}/consent`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            ...this.getAuthHeaders(),
          },
          body: JSON.stringify({
            sms_consent: smsConsent,
            whatsapp_consent: whatsappConsent,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update consent');
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating consent:', error);
      return null;
    }
  }

  async optOut(phone: string, channel: 'sms' | 'whatsapp' | 'all'): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE_URL}/v1/customers/opt-out`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phone, channel }),
      });

      return response.ok;
    } catch (error) {
      console.error('Error processing opt-out:', error);
      return false;
    }
  }

  async searchCustomers(query: string, limit = 10): Promise<Customer[]> {
    try {
      const response = await fetch(
        `${API_BASE_URL}/v1/customers/search?q=${encodeURIComponent(
          query
        )}&limit=${limit}`,
        {
          headers: {
            ...this.getAuthHeaders(),
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to search customers');
      }

      const data = await response.json();
      return data.customers || [];
    } catch (error) {
      console.error('Error searching customers:', error);
      return [];
    }
  }
}

export const customerService = new CustomerService();